import React, { useEffect, useMemo, useRef, useState } from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";

import { RecordsListProps } from "../../types/common";
import useUpdateEffect from "../../hooks/useUpdateEffect";
import useConfigSettings from "../../hooks/useConfigSettings";
import { useResizeObserver } from "../../hooks/useResizeObserver";

import {
  Loader,
  EmptyState,
  Button,
  TextInput,
  Dialog,
  ApiErrorBanner,
  Record,
  ExportExcelData,
  showNotification,
} from "../index";

import styles from "./RecordsList.module.scss";
import { CloseIcon } from "../Icons";

const NOOP_FUNC = () => "";

const RecordsList = (props: RecordsListProps) => {
  const {
    translationPrefix,
    queryResult,
    search,
    setSearch,
    iconComponent,
    addRecord,
    addRecordStatus,
    columnConfig,
    fileNamePrefix,
    fileNameSuffix,
    entityName,
    logExport,
    canExport,
    canAdd,
    getDialogTitle = NOOP_FUNC,
  } = props;
  const { data: records, isLoading } = queryResult;
  const { isLoading: isAddRecordLoading, isSuccess, isError } =
    addRecordStatus || {};
  const { t } = useTranslation();
  const { noteLengthLimit } = useConfigSettings();
  const canAddRecords = canAdd && !!addRecord && !!addRecordStatus;
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [isDialogActive, setIsDialogActive] = useState(false);
  const [newNote, setNewNote] = useState("");
  const trimmedNote = newNote.trim();
  const isNoteTooLong = newNote.length > noteLengthLimit;
  const characterLimitText = `${newNote.length.toLocaleString()}/${noteLengthLimit.toLocaleString()}`;

  const filteredRecords = useMemo(() => {
    const mappedRecords = (records || []).map((r) => ({
      ...r,
      createdDate: format(new Date(r.createdDate), "MMM d, ‘yy, h:mm a"),
    }));

    return debouncedSearch
      ? mappedRecords.filter((r) => {
          const searchText = (debouncedSearch || "").trim().toLowerCase();

          return [r.text, r.createdBy, r.createdDate].some((sourceText) =>
            (sourceText || "").toLowerCase().includes(searchText)
          );
        })
      : mappedRecords;
  }, [records, debouncedSearch]);

  useUpdateEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [search]);

  useEffect(() => {
    if (isDialogActive && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isDialogActive]);

  useEffect(() => {
    if (!isAddRecordLoading && isSuccess) {
      setIsDialogActive(false);
      contentRef.current?.scrollTo(0, 0);
    }
  }, [isAddRecordLoading, isSuccess]);

  useResizeObserver(contentRef, () => {
    const { clientHeight, scrollHeight } = contentRef.current!;

    setIsOverflowed(scrollHeight > clientHeight);
  });

  const openDialog = () => {
    setIsDialogActive(true);
    setNewNote("");
    addRecordStatus?.reset();
  };

  const onExportError = (error: any) => {
    showNotification(
      error.response?.data.message || error.message,
      undefined,
      CloseIcon
    );
  };

  const actions = [
    <Button
      key="1"
      disabled={!trimmedNote || isNoteTooLong}
      onClick={() => addRecord!(trimmedNote)}
      id={`${translationPrefix}-dialog-add-record-btn`}
    >
      {t(`${translationPrefix}.addRecord`)}
    </Button>,
    <Button
      key="2"
      variant="outlined"
      onClick={() => setIsDialogActive(false)}
      id={`${translationPrefix}-dialog-cancel-btn`}
    >
      {t("cancel")}
    </Button>,
  ];

  const hasNoRecords = !isLoading && !records?.length;
  const hasNoVisibleRecords = !!records?.length && !filteredRecords.length;

  return (
    <div
      className={classnames(
        styles.recordsList,
        canAddRecords && styles.recordsListEditable,
        isOverflowed && styles.recordsListOverflowed,
        (hasNoRecords || hasNoVisibleRecords) && styles.recordsListNoRecords
      )}
    >
      {isLoading && <Loader isBlocking />}
      {!!records?.length && (
        <div className={styles.recordsListTopControls}>
          {setSearch && (
            <TextInput
              value={search}
              wrapperClassName={styles.recordsListSearch}
              onChange={(e, value) => setSearch(value)}
              id={`${translationPrefix}-list-search-input`}
              placeholder={t(`${translationPrefix}.searchPlaceholder`)}
            />
          )}
          {canExport && (
            <ExportExcelData
              isIconButton
              onSuccess={logExport}
              onError={onExportError}
              dataRows={filteredRecords}
              columnConfig={columnConfig}
              buttonId={`${translationPrefix}-list-export-btn`}
              fileName={t(`${translationPrefix}.export.fileName`, {
                fileNamePrefix,
                fileNameSuffix,
              })}
              buttonLabel={t(`${translationPrefix}.export.buttonLabel`)}
            />
          )}
        </div>
      )}
      <div
        ref={contentRef}
        className={classnames(
          styles.recordsListContent,
          isOverflowed && styles.recordsListContentScrollable
        )}
      >
        {hasNoRecords && (
          <EmptyState
            iconComponent={iconComponent}
            className={styles.recordsListEmpty}
            title={t(`${translationPrefix}.emptyTitle`)}
            subtitle={t(`${translationPrefix}.emptySubtitle`, { entityName })}
          />
        )}
        {hasNoVisibleRecords && (
          <EmptyState
            iconComponent={iconComponent}
            className={styles.recordsListEmpty}
            title={t("recordsList.empty.title")}
            subtitle={t("recordsList.empty.subtitle")}
          />
        )}
        {filteredRecords.map((record) => (
          <Record key={record.id} data={record} />
        ))}
      </div>
      {canAddRecords && (
        <div className={styles.recordsListBottomControls}>
          <Button
            variant="outlined"
            onClick={openDialog}
            className={styles.recordsListAddRecordBtn}
            id={`${translationPrefix}-list-add-record-btn`}
          >
            {t(`${translationPrefix}.addRecord`)}
          </Button>
          <Dialog
            width={560}
            actions={actions}
            title={getDialogTitle(t(`${translationPrefix}.newRecord`))}
            isActive={isDialogActive}
            onClose={() => setIsDialogActive(false)}
          >
            {isError && (
              <ApiErrorBanner
                requestStatus={addRecordStatus!}
                id={`${translationPrefix}-dialog-error-banner`}
              />
            )}
            {isAddRecordLoading && <Loader isBlocking />}
            <TextInput
              isMultiline
              ref={inputRef}
              value={newNote}
              isError={isNoteTooLong}
              hintText={characterLimitText}
              onChange={(e, value) => setNewNote(value)}
              id={`${translationPrefix}-dialog-new-note-input`}
              messageClassName={styles.recordsListNewNoteMessage}
              errorText={
                isNoteTooLong
                  ? `${t("notes.characterLimit")} ${characterLimitText}`
                  : ""
              }
            />
          </Dialog>
        </div>
      )}
    </div>
  );
};

export default RecordsList;
